import React, { FC, SyntheticEvent, useEffect, useState } from 'react';
import { Form, Button, Col, Row, InputGroup } from 'react-bootstrap';
import validate from 'validate.js';
import Alert, { AlertType } from '../../components/Alert';
import { IUserData } from '../../../types/user';
import { useMergedState } from '../../../hooks/useMergedState';
import { IBaseForm, IBaseState } from '../../../types/types';
import { UseTranslate } from '../../../hooks/Translate';
import RequestHelper from '../../../apis/RequestHelper';
import { CountDown } from '../../components/CountDown';
import { profileContactPageConstraint } from '../../../constraints/ProfileContactPage';

export interface IUserContactInformationProps {
  email?: string;
  phoneNumber?: string;
  userId?: string;
  reloadUserDetail: () => void;
}

interface IUserContactInformation extends IBaseForm {
  phoneVerified: boolean;
  showConfirmCodeInput: boolean;
}

type PhoneVerificationState = Partial<IBaseState & IUserContactInformation>;

const UserContactInformation: FC<IUserContactInformationProps> = (props) => {
  const [showCountDown, setShowCountDown] = useState(false);
  const [state, setState] = useMergedState<PhoneVerificationState>({
    errors: {},
    phoneVerified: true,
    showConfirmCodeInput: false,
    alertProps: {
      type: AlertType.NONE
    }
  });
  const [translate] = UseTranslate();
  const [userData, setUserData] = useState<IUserData>({
    phoneNumber: props.phoneNumber,
    email: props.email
  });
  const [verificationRequestId, setVerificationRequestId] = useState<string>();
  const [confirmCode, setConfirmCode] = useState<string>('ds');

  useEffect(() => {
    setUserData({ phoneNumber: props.phoneNumber, email: props.email });
  }, [props]);

  const onFormSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    const fd = new FormData(e.target as HTMLFormElement);
    const values = Object.fromEntries<any>(fd.entries());

    const errors = validate(
      values,
      profileContactPageConstraint(
        translate('cas.registration.phoneStart'),
        translate('cas.registration.phoneLength'),
        translate('cas.registration.onlyNumber'),
        translate('cas.profile.value_required')
      ),
      { fullMessages: false }
    );
    if (errors) {
      for (const key in errors) {
        errors[key] = errors[key][0];
      }
      setState({ errors });
    } else {
      setState({ errors: {} });
      if (!state.showConfirmCodeInput) {
        requestPhoneChange();
      } else {
        changePhone();
      }
    }
  };

  const requestPhoneChange = async () => {
    setState({ errors: {} });
    try {
      const url = `${process.env.REACT_APP_CAS_API}/users/phone/changeRequest`;
      const res = await RequestHelper.cas.put(url, {
        phoneNumber: userData.phoneNumber
      });
      if (res.data.status === 'SUCCESS') {
        setVerificationRequestId(res.data.data.requestId);
        setState({ showConfirmCodeInput: true });
      }
    } catch (e) {
      setState({
        alertProps: {
          type: AlertType.ERROR,
          title: translate('cas.profile.errorNotice'),
          message: String(e)
        }
      });
    }
  };
  const changePhone = async () => {
    setState({ errors: {}, alertProps: { type: AlertType.NONE } });
    try {
      const url = `${process.env.REACT_APP_CAS_API}/users/phone/change`;
      const res = await RequestHelper.cas.put(url, {
        requestId: verificationRequestId,
        verificationCode: confirmCode
      });
      if (res.data.status === 'SUCCESS') {
        setVerificationRequestId('');
        setConfirmCode('');
        setState({
          errors: {},
          phoneVerified: true,
          showConfirmCodeInput: false
        });
        props.reloadUserDetail();
      }
    } catch (e) {
      setState({
        alertProps: {
          type: AlertType.ERROR,
          title: translate('cas.profile.errorNotice'),
          message: String(e)
        }
      });
    }
  };

  const retry = async () => {
    setState({ errors: {} });
    if (!showCountDown) {
      try {
        setShowCountDown(true);
        const url = `${process.env.REACT_APP_CAS_API}/users/phone/change/refreshOTP`;
        await RequestHelper.cas.put(url, {
          requestId: verificationRequestId
        });
      } catch (e) {
        setState({
          alertProps: {
            type: AlertType.ERROR,
            title: translate('cas.registration.errorNotice'),
            message: String(e)
          }
        });
      }
    }
  };

  const validateOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    userData[event.currentTarget.name] = event.currentTarget.value;
    setState({ phoneVerified: false });
    const errors = validate(
      { phoneNumber: event.currentTarget.value },
      profileContactPageConstraint(
        translate('cas.registration.phoneStart'),
        translate('cas.registration.phoneLength'),
        translate('cas.registration.onlyNumber'),
        translate('cas.profile.value_required')
      ),
      { fullMessages: false }
    );
    if (errors) {
      for (const key in errors) {
        errors[key] = errors[key][0];
      }
      setState({ errors });
    } else {
      setState({ errors: {} });
      setUserData(userData);
    }
  };

  const onCancelClick = (e: any) => {
    setShowCountDown(false);
    // to let CountDown component unmount
    setTimeout(() => {
      setState({
        errors: {},
        phoneVerified: true,
        showConfirmCodeInput: false
      });
    }, 300);
    setUserData({
      phoneNumber: props.phoneNumber,
      email: props.email
    });
  };
  const onInputChange = (e: any) => {
    setConfirmCode(e.target.value);
  };

  const controlStyle = {
    height: 51,
    background: '#F4F5F7',
    border: '1px solid #E6E7E8',
    borderRadius: 6
  };

  const alertPadding = { paddingLeft: 0, paddingRight: 5 };

  return (
    <div className='cas-profile-content'>
      <div className='container-title'>
        {translate('cas.profile.contactPageTitle')}
      </div>

      <div className='col-md-3' style={alertPadding}>
        <Alert
          type={state.alertProps!.type}
          title={state.alertProps!.title}
          message={state.alertProps!.message}
        />
      </div>

      <Form noValidate onSubmit={onFormSubmit}>
        <Row>
          <Col md={3} className='margin-r-10'>
            <InputGroup>
              <Form.Group className='mb-3' controlId='phoneNumber' style={{ width: '100%' }}>
                <Form.Label className='small user-icon'>
                  {' '}
                  {translate('cas.profile.phoneNumber')}
                </Form.Label>
                <Form.Control
                  type='text'
                  value={userData.phoneNumber || ''}
                  name='phoneNumber'
                  placeholder={translate('cas.profile.enterPhoneNumber')}
                  isInvalid={'phoneNumber' in state.errors!}
                  onChange={validateOnChange}
                  required
                  style={controlStyle}
                />
                {state.phoneVerified ? (
                  <Form.Text className='text-style text-muted'>
                    {translate('cas.profile.verified')}
                  </Form.Text>
                ) : (
                  ''
                )}

                {state.phoneVerified ? (
                  <span className='toggle-visible-checked' />
                ) : (
                  <span
                    className='toggle-visible-cancel'
                    onClick={onCancelClick}
                  />
                )}

                <Form.Control.Feedback type='invalid'>
                  {state.errors?.phoneNumber}
                </Form.Control.Feedback>

              </Form.Group>
            </InputGroup>
          </Col>

          {state.showConfirmCodeInput ? (
            <Col md={3}>
              <InputGroup>
                <Form.Group className='mb-3' controlId='confirmCode'>
                  <Form.Label className='small user-icon'>
                    {' '}
                    {translate('cas.profile.confirmCode')}
                  </Form.Label>
                  <Form.Control
                    type='text'
                    onChange={onInputChange}
                    name='confirmCode'
                    required
                    style={controlStyle}
                  />

                  <Form.Text>
                    <div
                      className={`text-style resend-text ${showCountDown ? 'resend-disabled' : ''
                        }`}
                      onClick={retry}
                    >
                      {' '}
                      {translate('cas.profile.resendCode')}
                    </div>
                  </Form.Text>
                  {
                    <CountDown
                      seconds={60}
                      showCountDown={showCountDown}
                      onCountDownFinished={() => {
                        setShowCountDown(false);
                      }}
                    />
                  }

                  <Form.Control.Feedback type='invalid'>
                    {state.errors?.phoneNumber}
                  </Form.Control.Feedback>
                </Form.Group>
              </InputGroup>
            </Col>
          ) : (
            ''
          )}
          {!state.phoneVerified && !state.errors?.phoneNumber ? (
            <Col md={3}>
              <Form.Group className='mb-3' controlId='confirmCode'>
                <Form.Label className='small user-icon'></Form.Label>
                <Button
                  type='submit'
                  className='mt-3 custom-submit-button margin-top-3'
                >
                  {state.showConfirmCodeInput
                    ? translate('cas.profile.submit')
                    : translate('cas.profile.verify')}
                </Button>
              </Form.Group>
            </Col>
          ) : (
            ''
          )}
        </Row>

        <Row>
          <Col md={3}>
            <Form.Group className='mb-3' controlId='email'>
              <Form.Label className='small user-icon'>
                {' '}
                {translate('cas.profile.email')}
              </Form.Label>
              <Form.Control
                type='text'
                defaultValue={userData.email}
                name='email'
                placeholder={translate('cas.profile.enterEmail')}
                style={controlStyle}
              />
            </Form.Group>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default UserContactInformation;
